import React, {
  useId,
  useState,
} from 'react';

const Input = ({
  label,
  error,
  helperText,
  className = '',
  type = 'text',
  id,
  ...props
}) => {
  /*
   * ---------------------------------------------------------
   * PASSWORD VISIBILITY
   * ---------------------------------------------------------
   *
   * Any Input rendered with type="password" automatically gets
   * a show/hide control.
   *
   * Each field maintains its own visibility state, so:
   *
   * - New Password
   * - Confirm Password
   * - Kid PIN
   *
   * can all be shown/hidden independently.
   */
  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  /*
   * React-generated ID provides a proper label/input
   * association when the calling component does not supply
   * its own ID.
   */
  const generatedId =
    useId();

  const inputId =
    id ||
    generatedId;

  const isPassword =
    type === 'password';

  const resolvedType =
    isPassword &&
    showPassword
      ? 'text'
      : type;

  const toggleLabel =
    showPassword
      ? 'Hide password'
      : 'Show password';

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
        >
          {label}
        </label>
      )}

      <div
        className={
          isPassword
            ? 'relative'
            : ''
        }
      >
        <input
          id={inputId}
          type={resolvedType}
          className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            isPassword
              ? 'pr-11'
              : ''
          } ${
            error
              ? 'border-red-500 dark:border-red-500'
              : 'border-gray-300 dark:border-slate-600'
          }`}
          {...props}
        />

        {isPassword && (
          <button
            type="button"
            onClick={() =>
              setShowPassword(
                (current) =>
                  !current
              )
            }
            className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset rounded-r-lg disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={
              toggleLabel
            }
            aria-pressed={
              showPassword
            }
            title={
              toggleLabel
            }
            disabled={
              props.disabled
            }
          >
            {showPassword ? (
              /*
               * Eye-off icon
               */
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path d="m3 3 18 18" />
                <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                <path d="M9.88 4.24A9.6 9.6 0 0 1 12 4c5 0 8.5 4 9.5 6-.46.92-1.46 2.36-2.94 3.61" />
                <path d="M6.61 6.61C4.62 7.9 3.3 9.7 2.5 11c1 2 4.5 6 9.5 6 1.1 0 2.1-.2 3-.55" />
              </svg>
            ) : (
              /*
               * Eye icon
               */
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                <circle
                  cx="12"
                  cy="12"
                  r="2.5"
                />
              </svg>
            )}
          </button>
        )}
      </div>

      {error && (
        <p
          className="mt-1 text-sm text-red-600 dark:text-red-400"
          role="alert"
        >
          {error}
        </p>
      )}

      {!error &&
        helperText && (
          <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
            {helperText}
          </p>
        )}
    </div>
  );
};

export default Input;